import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  useColorAssetForm,
  useColorPickerModalState,
} from "../hooks/useColorPickerModal";
import {
  createColorAssetForScope,
  createImageAssetForScope,
} from "../lib/asset-create";
import { TAB_LABEL } from "../lib/asset-modal-tabs";
import { buildAssetCreateBody } from "../lib/color-picker";
import { invalidateAssets } from "../lib/queries";
import { AssetModalChrome } from "./AssetModalChrome";
import { ColorAssetFormFields, ColorPickerFields } from "./ColorPickerFields";
import type { AssetScope } from "./gallery/galleryTree";
import {
  ImageAssetUploadFields,
  useImageFileSelection,
} from "./ImageAssetUploadFields";

interface AddLibraryAssetModalProps {
  isOpen: boolean;
  /** Which gallery asset bucket to save into. Org-scoped assets are created
   *  personal then promoted via share-with-org. */
  assetScope: AssetScope;
  onClose: () => void;
}

type AddLibraryAssetTab = "image" | "color";

const TABS = [
  { id: "image" as AddLibraryAssetTab, label: TAB_LABEL.image },
  { id: "color" as AddLibraryAssetTab, label: TAB_LABEL.color },
];

/** Gallery "Add Asset" modal: upload an image or pick a color. */
export function AddLibraryAssetModal({
  isOpen,
  assetScope,
  onClose,
}: AddLibraryAssetModalProps) {
  const [activeTab, setActiveTab] = useState<AddLibraryAssetTab>("image");
  const pickerState = useColorPickerModalState({
    isOpen,
    initialHex: "#ffffff",
  });
  const colorForm = useColorAssetForm(isOpen, true);
  const { file, setFile, previewUrl } = useImageFileSelection(isOpen);
  const [imageName, setImageName] = useState("");
  const queryClient = useQueryClient();

  const isUploadTab = activeTab === "image";

  const saveAsset = useMutation({
    mutationFn: async () => {
      if (isUploadTab) {
        if (!file) throw new Error("No file selected");
        return createImageAssetForScope(file, imageName.trim(), assetScope);
      }
      return createColorAssetForScope(
        buildAssetCreateBody(colorForm.name.trim(), pickerState.selectedHex),
        assetScope,
      );
    },
    // Invalidate on settled rather than success: org-scoped saves create the
    // personal asset first, so a failed share-with-org promotion still leaves
    // a new asset that the lists must pick up.
    onSettled: () => invalidateAssets(queryClient),
    onSuccess: onClose,
  });

  const resetMutation = saveAsset.reset;
  useEffect(() => {
    if (isOpen) {
      setActiveTab("image");
      setImageName("");
      resetMutation();
    }
  }, [isOpen, resetMutation]);

  if (!isOpen) return null;

  const trimmedName = (isUploadTab ? imageName : colorForm.name).trim();
  const okDisabled =
    saveAsset.isPending || trimmedName.length === 0 || (isUploadTab && !file);

  return (
    <AssetModalChrome
      ariaContext="add asset"
      title="Add Asset"
      subtitle="Upload an image or choose a color to save it to your assets."
      tabs={TABS}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      primary={{
        label: saveAsset.isPending ? "Saving…" : "Save",
        disabled: okDisabled,
        onClick: () => saveAsset.mutate(),
      }}
      onClose={onClose}
    >
      {isUploadTab ? (
        <ImageAssetUploadFields
          file={file}
          previewUrl={previewUrl}
          onSelectFile={setFile}
          name={imageName}
          onNameChange={setImageName}
        />
      ) : (
        <>
          <ColorPickerFields pickerState={pickerState} />
          <div className="mt-6">
            <ColorAssetFormFields assetForm={colorForm} />
          </div>
        </>
      )}

      {saveAsset.isError && (
        <p className="mt-4 text-sm text-red-400" role="alert">
          Couldn&apos;t save this asset. Please try again.
        </p>
      )}
    </AssetModalChrome>
  );
}
